/**
 * Frontend image compression utilities for immediate user feedback
 * and reducing upload sizes before sending to backend
 */

export interface CompressionOptions {
  maxSizeBytes: number;
  maxWidth: number;
  maxHeight: number;
  quality: number;
  format: 'jpeg' | 'webp';
}

export interface CompressionResult {
  compressedFile: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  dimensions: {
    width: number;
    height: number;
  };
}

export class ImageCompressionUtils {
  /**
   * Compress an image file on the frontend
   */
  static async compressImage(
    file: File,
    options: Partial<CompressionOptions> = {}
  ): Promise<CompressionResult> {
    const defaultOptions: CompressionOptions = {
      maxSizeBytes: 2 * 1024 * 1024, // 2MB
      maxWidth: 1920,
      maxHeight: 1920,
      quality: 0.85,
      format: 'jpeg',
    };

    const config = { ...defaultOptions, ...options };
    const originalSize = file.size;

    console.log('[ImageCompressionUtils] Starting compression', {
      fileName: file.name,
      originalSize,
      config,
    });

    try {
      // Create canvas and context
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Load image
      const img = await this.loadImage(file);
      
      // Calculate new dimensions
      const { width: newWidth, height: newHeight } = this.calculateDimensions(
        img.width,
        img.height,
        config.maxWidth,
        config.maxHeight
      );

      // Set canvas dimensions
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Draw and compress image
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Try different quality levels if needed
      let quality = config.quality;
      let compressedBlob: Blob | null = null;
      let attempts = 0;
      const maxAttempts = 5;

      do {
        compressedBlob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(
            resolve,
            `image/${config.format}`,
            quality
          );
        });

        if (!compressedBlob) {
          throw new Error('Failed to compress image');
        }

        // If still too large, reduce quality
        if (compressedBlob.size > config.maxSizeBytes && attempts < maxAttempts) {
          quality = Math.max(0.2, quality - 0.15); // Reduce quality but not below 20%
          attempts++;
          console.log('[ImageCompressionUtils] Reducing quality', {
            attempt: attempts,
            currentSize: compressedBlob.size,
            targetSize: config.maxSizeBytes,
            quality,
          });
        }
      } while (compressedBlob.size > config.maxSizeBytes && attempts < maxAttempts);

      // Create compressed file
      const compressedFile = new File(
        [compressedBlob],
        this.generateCompressedFileName(file.name, config.format),
        {
          type: `image/${config.format}`,
          lastModified: Date.now(),
        }
      );

      const result: CompressionResult = {
        compressedFile,
        originalSize,
        compressedSize: compressedFile.size,
        compressionRatio: originalSize / compressedFile.size,
        dimensions: {
          width: newWidth,
          height: newHeight,
        },
      };

      console.log('[ImageCompressionUtils] Compression completed', {
        originalSize,
        compressedSize: result.compressedSize,
        compressionRatio: result.compressionRatio.toFixed(2),
        finalQuality: quality,
        attempts,
        dimensions: result.dimensions,
      });

      return result;
    } catch (error) {
      console.error('[ImageCompressionUtils] Compression failed', error);
      throw new Error(`Image compression failed: ${error}`);
    }
  }

  /**
   * Load image from file
   */
  private static loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      
      // Timeout after 30 seconds (mobile can be slow loading large images)
      const timeoutId = setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Image loading timed out'));
      }, 30000);
      
      img.onload = () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };
      img.onerror = () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image'));
      };
      img.src = objectUrl;
    });
  }

  /**
   * Calculate optimal dimensions while maintaining aspect ratio
   */
  private static calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    const aspectRatio = originalWidth / originalHeight;

    let width = Math.min(originalWidth, maxWidth);
    let height = Math.min(originalHeight, maxHeight);

    // Maintain aspect ratio
    if (width / height > aspectRatio) {
      width = Math.round(height * aspectRatio);
    } else {
      height = Math.round(width / aspectRatio);
    }

    return { width, height };
  }

  /**
   * Generate compressed file name
   */
  private static generateCompressedFileName(originalName: string, format: string): string {
    const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
    return `${nameWithoutExt}_compressed.${format}`;
  }

  /**
   * Check if image needs compression
   */
  static needsCompression(file: File, maxSizeBytes: number = 2 * 1024 * 1024): boolean {
    return file.size > maxSizeBytes;
  }

  /**
   * Get optimal compression settings based on file characteristics
   */
  static getOptimalSettings(file: File): Partial<CompressionOptions> {
    const isLargeFile = file.size > 5 * 1024 * 1024; // 5MB
    const isVeryLargeFile = file.size > 10 * 1024 * 1024; // 10MB
    const isPNG = file.type.includes('png');
    
    // Detect mobile — more aggressive compression to avoid memory issues
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    let quality = 0.85;
    let maxWidth = 1920;
    let maxHeight = 1920;

    if (isMobile) {
      // Mobile: cap at 1200px to avoid canvas memory issues
      quality = isVeryLargeFile ? 0.65 : isLargeFile ? 0.7 : 0.75;
      maxWidth = 1200;
      maxHeight = 1200;
    } else if (isVeryLargeFile) {
      quality = 0.75;
      maxWidth = 1600;
      maxHeight = 1600;
    } else if (isLargeFile) {
      quality = 0.8;
      maxWidth = 1800;
      maxHeight = 1800;
    }

    const format = isPNG ? 'jpeg' : 'jpeg';

    return {
      quality,
      maxWidth,
      maxHeight,
      format,
      maxSizeBytes: 2 * 1024 * 1024,
    };
  }

  /**
   * Create a preview/thumbnail version of the image
   */
  static async createThumbnail(
    file: File,
    maxSize: number = 300
  ): Promise<File> {
    const result = await this.compressImage(file, {
      maxWidth: maxSize,
      maxHeight: maxSize,
      quality: 0.8,
      maxSizeBytes: 100 * 1024, // 100KB max for thumbnails
      format: 'jpeg',
    });

    return result.compressedFile;
  }

  /**
   * Validate image file before processing
   */
  static validateImageFile(file: File): { isValid: boolean; error?: string } {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return { isValid: false, error: 'File must be an image' };
    }

    // Check supported formats
    const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!supportedFormats.includes(file.type)) {
      return { isValid: false, error: 'Unsupported image format. Please use JPEG, PNG, or WebP' };
    }

    // Check file size (max 50MB for processing)
    const maxFileSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxFileSize) {
      return { isValid: false, error: 'Image file is too large. Maximum size is 50MB' };
    }

    // Check minimum size (at least 100x100)
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        if (img.width < 100 || img.height < 100) {
          resolve({ isValid: false, error: 'Image must be at least 100x100 pixels' });
        } else {
          resolve({ isValid: true });
        }
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        resolve({ isValid: false, error: 'Invalid image file' });
      };
      img.src = URL.createObjectURL(file);
    }) as any;
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Calculate compression percentage
   */
  static getCompressionPercentage(originalSize: number, compressedSize: number): number {
    return Math.round(((originalSize - compressedSize) / originalSize) * 100);
  }
}